import scrollToElem from '@/scripts/lib/scrollToElem';
import AbortController from '@/scripts/polyfills/AbortController';
import Pagination from './Pagination/Pagination';
import Events from '@/scripts/lib/Events';

/** @typedef {'content'|'loading'|'empty'|'error'} Mode */

/**
 * @typedef {{
 *    pageSize: number,
 *    pagination: Pagination,
 *    scrollTo: HTMLElement,
 *    initialPage: number,
 * }} Options
 */

/** 
 * @abstract 
 * @template T
 */
export default class AjaxList {
   /** @type {Events<'change.mode'|'update.before'|'request.processed'>} */
   events = new Events();

   /** @type {Mode} */
   mode = 'loading';

   /** @type {T[][]} */
   _allData = [];
   _numberOfItems = 0;

   _requestId = 0;
   _abortController = new AbortController();

   /**
    * @param {HTMLElement} root 
    * @param {Options} param1 
    */
   constructor(root, {
      pageSize = 10, pagination, scrollTo, initialPage = 0,
   } = {}) {
      if (new.target === AjaxList) {
         throw new TypeError('Cannot construct Abstract instances directly');
      }

      this.$root = root;
      this.$scrollTo = scrollTo || root;

      this.page = initialPage;
      this._pagination = pagination;
      this._pageSize = pageSize;
   }

   init() {
      this._initEvents();
   }

   async start() {
      await this.update({ scroll: false });
   }

   /** @param {Mode} mode */
   setMode(mode) {
      this.mode = mode;
      this.$root.dataset.mode = mode;
      this.events.emit('change.mode', mode);
   }

   _initEvents() {
      this._pagination.events.addListener('change', (page) => {
         this.openPage(page - 1);
      });
   }

   async update({ scroll = true } = {}) {
      this._abortController.abort();
      this._abortController = new AbortController();

      const loadingTimer = setTimeout(() => {
         this.setMode('loading');
      }, 250);

      const scrollPromise = (scroll) ?
         scrollToElem(this.$scrollTo, 5) : Promise.resolve();

      this.events.emit('update.before');

      try {
         await Promise.all([
            this._updatePageData(),
            scrollPromise,
         ]);
      } catch (e) {
         if (e.name === 'AbortError') return;
         if (typeof e !== 'string') throw e;
         this.setMode('error');
         this._showError(e);
         return;
      } finally {
         clearTimeout(loadingTimer);
      }

      if (this.page > Math.max(this._numberOfPages - 1, 0)) {
         this.page = this._numberOfPages - 1;
         this.update({ scroll: false });
         return;
      }

      this.$root.dataset.numberOfPages = this._numberOfPages;

      if (this._numberOfPages) {
         this.setMode('content');
      } else {
         this.setMode('empty');
      }

      this.render();
   }

   async _updatePageData() {
      if (this.pageItems.length) return;
      await this._processRequest();

      this._allData.length = this._numberOfPages;
   }

   async openPage(page) {
      page = Math.min(Math.max(page, 0), this._numberOfPages - 1);
      if (this.page === page) return;
      this.page = page;

      this.update();
   }

   render() {
      this.renderCurPage();
      this._updatePagination();
   }

   _updatePagination() {
      this._pagination.pages = this._numberOfPages;
      this._pagination.cur = this.page + 1;
      this._pagination.render();
   }

   /** @virtual */
   renderCurPage() {
      throw new TypeError('You must implement _renderCurPage() method');
   }

   /** 
    * Load current page data
    * @virtual 
    * 
    * @example
    * 
    * ```js
    * const resp = await fetch('...', { signal });
    * return { total, items }
    * ```
    * @return { total: number, items: any[] }
    */
   async _makeRequest() {
      throw new TypeError('You must implement _makeRequest(signal) method');
   }

   /** @virtual */
   _showError() {
      throw new TypeError('You must implement _showError(msg) method');
   }

   async _processRequest() {
      const signal = this._abortController.signal;
      const requestId = ++this._requestId;
      const { total, items } = await this._makeRequest(signal);
      if (requestId !== this._requestId) {
         throw new DOMException('', 'AbortError');
      }
      this._numberOfItems = total;
      this.pageItems = items;

      // TODO: remove requestAnimationFrame when real pagination
      requestAnimationFrame(() => {
         this.events.emit('request.processed');
      });
   }

   clear() {
      this.page = 0;
      this._allData = [];
      this._numberOfItems = 0;
      this._requestId++;
      this._abortController.abort();
   }

   get _numberOfPages() {
      return Math.ceil(this._numberOfItems / this._pageSize);
   }

   get pageItems() {
      return this._allData[this.page] || [];
   }

   set pageItems(items) {
      this._allData[this.page] = items;
   }

   get _itemsOffset() {
      return this._pageSize * this.page;
   }
}
